import { elementsOverlap, exists, shuffleArray } from '@/helpers/utilities';

export let mixin = ({ node = null, config = {} } = {}) => {
    if (!node) return false;

    let state = {
        node,
        config,
        debouncer: false,
        sqSize: 0,
        gridWidth: null,
        gridHeight: null,
        justEmpty: false,
        resizeTimer: null,
        customEvent: 'ctacGridLayoutComplete',
    };

    let module = {
        hasRandomBorder() {
            if (Math.floor(Math.random() * 10) + 1 < 4) return false;
            return true;
        },
        hasRandomFill() {
            if (Math.floor(Math.random() * 20) + 1 < 5) return false;
            return true;
        },
        getBorder() {
            return `
            <div class="grid-sq-border"></div>
            `;
        },
        getBrackets() {
            return `
            <img class="grid-sq-brackets" src="/wp-content/themes/ctac/assets/images/svg/grid-sq-brackets.svg" />
            `;
        },
        getCorners() {
            return `
            <img class="grid-sq-corners" src="/wp-content/themes/ctac/assets/images/svg/grid-sq-corners.svg" />
            `;
        },
        getFill() {
            return `
            <img class="grid-sq-fill" src="/wp-content/themes/ctac/assets/images/svg/grid-sq-fill.svg" />
            `;
        },
        getDimensions() {
            state.gridWidth = state.node.offsetWidth;
            state.gridHeight = state.node.offsetHeight;
            state.sqSize = this.getSqSize();
        },
        getSqSize() {
            return parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--grid-sq-size')) * 10;
        },
        checkOverlap(sq) {
            //hide grid squares that are overlapping key elements
            let overlappingElements = `.main-nav .menu-main-menu-container, [data-grid-region]`;
            let elements = document.querySelectorAll(overlappingElements);
            if (!elements.length) return;

            return [...elements].some((element) => elementsOverlap(element, sq));
        },
        hideOverlaps() {
            let squares = shuffleArray([...state.node.children]);

            requestAnimationFrame(() => {
                let index = 0;

                for (let sq of squares) {
                    if (this.checkOverlap(sq)) {
                        sq.classList.add('is-hidden');
                    }
                }
            });
        },
        animateSquares() {
            let squares = shuffleArray([...state.node.children]);

            //using for..of loop for performance
            requestAnimationFrame(() => {
                let index = 0;

                for (let sq of squares) {
                    if (this.checkOverlap(sq)) {
                        sq.classList.add('is-hidden');
                    }

                    let delay = (index + 1) * 8;

                    setTimeout(() => {
                        sq.classList.add('js-is-ready');
                    }, delay);

                    index++;
                }
            });
        },
        makeLayout() {
            state.node.innerHTML = '';
            let availableCols = Math.floor(state.gridWidth / state.sqSize);
            let availableRows = Math.floor(state.gridHeight / state.sqSize);
            let squares = Array.from({ length: availableRows * availableCols });

            let index = 0;

            //using for..of loop for performance
            for (let sq of squares) {
                let div = document.createElement('div');

                /* randomizes border and fill but doesn't allow 
                two borderless squares next to eachother */
                let hasBorder = index == 0 || this.hasRandomBorder();
                let hasFill = index == 0 || this.hasRandomFill();

                if (hasBorder || state.justEmpty) {
                    div.insertAdjacentHTML('afterbegin', this.getBorder());
                    div.insertAdjacentHTML('afterbegin', this.getCorners());
                    if (state.node.dataset.hasBrackets) {
                        div.insertAdjacentHTML('afterbegin', this.getBrackets());   
                    }
                }
                if (hasFill) {
                    div.insertAdjacentHTML('afterbegin', this.getFill());
                }
                if (hasFill && !hasBorder) {
                    div.classList.add('is-borderless');
                }
                if (!hasFill && !hasBorder) {
                    div.classList.add('is-empty');
                }


                if (!hasBorder) {
                    state.justEmpty = true;
                } else {
                    state.justEmpty = false;
                }

                //add to DOM and handle overlap
                state.node.appendChild(div);
                index++;
            }

            this.animateSquares();

            window.dispatchEvent(new CustomEvent(state.customEvent));
        },
        debounceResize() {
            state.debouncer = true;
            setTimeout(() => {
                state.debouncer = false;
            }, 500);
        },
        onWindowResizeEnd() {
            if (state.debouncer) return;
            this.debounceResize();

            state.node.classList.add('is-blurred');

            clearTimeout(state.resizeTimer);
            state.resizeTimer = setTimeout(() => {
                state.node.classList.remove('is-blurred');

                this.getDimensions();
                this.makeLayout();
            }, 200);
        },
        onGridReflow() {
            this.makeLayout();
        },
        setEventBindings() {
            window.addEventListener('resize', this.onWindowResizeEnd.bind(this));
            if (state.node.dataset.reflow) {
                window.addEventListener('reflowGrid', this.onGridReflow.bind(this));
            }
            window.addEventListener('stickyNavReset', this.hideOverlaps.bind(this));
        },
        init() {
            if (!exists(state.node)) return;

            requestAnimationFrame(() => {
                this.getDimensions();
                this.makeLayout();
                this.setEventBindings();
            });
        },
    };

    return module.init();
};

export default {
    init({ selector, config = {} }) {
        let selected = document.querySelectorAll(selector);
        if (!selected.length) return;

        return [...selected].map((node, index) => {
            return mixin({ node, config });
        });
    },
};
